import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  crmStaticUrl,
  extractCrmError,
  userCanAccessCrmMarketing,
} from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  ImagePlus,
  Loader2,
  MoreHorizontal,
  Sparkles,
  Trash2,
} from "lucide-react";

const PROMPT_PLACEHOLDER =
  "Новое поступление оригинальных передних тормозных колодок LADA Granta. Есть в наличии. Сделать продающий пост, показать товар крупным планом, использовать светлый современный фон. Акцент на оригинальное качество и наличие в магазине.";

const STATUS_LABELS = {
  draft: "Черновик",
  generating: "Генерируется",
  ready: "Готово",
  moderation: "На модерации",
  approved: "Одобрено",
  scheduled: "Запланировано",
  publishing: "Публикуется",
  published: "Опубликовано",
  partial: "Частично опубликовано",
  failed: "Ошибка",
  cancelled: "Отменено",
};

const FILTERS = [
  { id: "all", label: "Все" },
  { id: "draft", label: "Черновики" },
  { id: "generating", label: "Генерация" },
  { id: "ready", label: "Готовы" },
  { id: "scheduled", label: "Запланированы" },
  { id: "published", label: "Опубликованы" },
  { id: "errors", label: "Ошибки" },
];

const REWRITE_ACTIONS = [
  { id: "shorter", label: "Сделать короче" },
  { id: "selling", label: "Сделать более продающим" },
  { id: "emoji", label: "Добавить эмодзи" },
  { id: "calmer", label: "Сделать спокойнее" },
  { id: "cta", label: "Изменить CTA" },
  { id: "alternative", label: "Другой вариант" },
  { id: "fix", label: "Исправить ошибки" },
];

const TABS = [
  { id: "create", label: "Создать" },
  { id: "campaigns", label: "Кампании" },
  { id: "calendar", label: "Календарь" },
  { id: "templates", label: "Шаблоны" },
  { id: "settings", label: "Настройки" },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function statusClass(status) {
  if (status === "published") return "bg-emerald-50 text-emerald-700";
  if (status === "failed") return "bg-rose-50 text-rose-700";
  if (status === "partial") return "bg-amber-50 text-amber-800";
  if (status === "scheduled") return "bg-sky-50 text-sky-800";
  if (status === "generating" || status === "publishing") return "bg-indigo-50 text-indigo-700";
  return "bg-slate-100 text-slate-700";
}

function campaignChannels(campaign) {
  const items = [];
  if (campaign?.publish_to_instagram_post) items.push("instagram_post");
  if (campaign?.publish_to_whatsapp) items.push("whatsapp");
  return items;
}

function channelLabel(id) {
  if (id === "instagram_post") return "Instagram";
  if (id === "whatsapp") return "WhatsApp";
  return id;
}

function coverOf(campaign) {
  const slide = (campaign?.slides || []).find((item) => item.cover_image_path || item.final_image_path);
  return slide?.cover_image_path || slide?.final_image_path || campaign?.cover_image_path;
}

function latestPublication(campaign, channel) {
  const items = (campaign?.publications || []).filter((item) => item.channel === channel);
  const published = items.find((item) => item.status === "published");
  return published || items[0] || null;
}

function toDateInput(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toTimeInput(value) {
  const date = value ? new Date(value) : new Date(Date.now() + 60 * 60 * 1000);
  if (Number.isNaN(date.getTime())) return "09:00";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDateTime(dateStr, timeStr) {
  return new Date(`${dateStr}T${timeStr}:00`);
}

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    days.push(day);
  }
  return days;
}

export default function CrmMarketingPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canWrite = !!user && (user.is_admin || user.role === "admin" || user.role === "manager");

  const [tab, setTab] = useState("create");
  const [capabilities, setCapabilities] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [home, setHome] = useState({ upcoming: [], recent: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [campaign, setCampaign] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [channels, setChannels] = useState(["instagram_post", "whatsapp"]);
  const [imageCount, setImageCount] = useState(1);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [imageStyle, setImageStyle] = useState("");
  const [useBrandStyle, setUseBrandStyle] = useState(true);
  const [includePrice, setIncludePrice] = useState(false);
  const [includeAddress, setIncludeAddress] = useState(true);
  const [includeLogo, setIncludeLogo] = useState(false);
  const [price, setPrice] = useState("");
  const [cta, setCta] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [formatMode, setFormatMode] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(toDateInput(new Date(Date.now() + 86400000)));
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarItems, setCalendarItems] = useState([]);
  const [templateForm, setTemplateForm] = useState(null);
  const [rewriteMenu, setRewriteMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const autosaveTimer = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!authLoading && !userCanAccessCrmMarketing(user)) navigate("/");
  }, [authLoading, navigate, user]);

  const availableChannels = useMemo(() => {
    const items = [];
    if (capabilities?.instagram_post?.available !== false) {
      items.push({ id: "instagram_post", label: "Instagram Post" });
    }
    if (capabilities?.whatsapp_status?.available !== false) {
      items.push({ id: "whatsapp", label: "WhatsApp Status" });
    }
    return items;
  }, [capabilities]);

  const applyCampaign = useCallback((next) => {
    setCampaign(next);
    if (!next) return;
    setPrompt(next.prompt_text || next.ai_instruction || next.description || "");
    setChannels(campaignChannels(next).length ? campaignChannels(next) : ["instagram_post", "whatsapp"]);
    setImageCount(next.image_count || 1);
    setImageStyle(next.image_style || "");
    setUseBrandStyle(next.use_brand_style !== false);
    setIncludePrice(!!next.include_price);
    setIncludeAddress(next.include_address !== false);
    setIncludeLogo(!!next.include_logo);
    setPrice(next.price || "");
    setCta(next.instagram_cta || "");
    setExtraNotes(next.extra_notes || "");
    setFormatMode(next.format_mode || "auto");
    setSavedAt(next.last_saved_at || null);
  }, []);

  const loadStudio = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [caps, homeData, templateData, campaignData] = await Promise.all([
        crmApi.getStudioCapabilities(token),
        crmApi.getStudioHome(token),
        crmApi.listStudioTemplates(token),
        crmApi.listStudioCampaigns(token, { status: campaignFilter, limit: 80 }),
      ]);
      setCapabilities(caps);
      setHome(homeData);
      setTemplates(templateData);
      setCampaigns(campaignData);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить студию"));
    } finally {
      setLoading(false);
    }
  }, [token, campaignFilter]);

  useEffect(() => {
    loadStudio();
  }, [loadStudio]);

  const loadCalendar = useCallback(async () => {
    if (!token) return;
    const start = new Date(calendarMonth.year, calendarMonth.month, 1);
    const end = new Date(calendarMonth.year, calendarMonth.month + 1, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    try {
      const items = await crmApi.getStudioCalendar(token, { date_from: iso(start), date_to: iso(end) });
      setCalendarItems(items);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить календарь"));
    }
  }, [token, calendarMonth]);

  useEffect(() => {
    if (tab === "calendar") loadCalendar();
  }, [tab, loadCalendar]);

  useEffect(() => {
    if (!campaign || campaign.status !== "generating" || !token) return undefined;
    const timer = setInterval(async () => {
      try {
        const next = await crmApi.getStudioCampaign(token, campaign.id);
        applyCampaign(next);
        if (next.status !== "generating") {
          setCampaigns((prev) => prev.map((item) => (item.id === next.id ? next : item)));
        }
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось обновить статус генерации"));
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [applyCampaign, campaign, token]);

  const queueAutosave = (patch) => {
    if (!canWrite || !campaign?.id) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        const next = await crmApi.patchStudioCampaign(token, campaign.id, patch);
        applyCampaign(next);
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось сохранить черновик"));
      }
    }, 700);
  };

  const toggleChannel = (id) => {
    setChannels((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
      if (campaign?.id) queueAutosave({ channels: next.length ? next : prev });
      return next.length ? next : prev;
    });
  };

  const handleGenerate = async () => {
    if (!canWrite) return;
    if (!prompt.trim()) {
      toast.error("Опишите, что хотите рассказать клиентам.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        prompt: prompt.trim(),
        channels,
        image_count: imageCount,
        image_style: imageStyle || null,
        use_brand_style: useBrandStyle,
        include_price: includePrice,
        include_address: includeAddress,
        include_logo: includeLogo,
        extra_notes: extraNotes || null,
        format_mode: formatMode,
        price: price || null,
      };
      const created = campaign?.id
        ? await crmApi.patchStudioCampaign(token, campaign.id, {
            prompt_text: payload.prompt,
            channels: payload.channels,
            image_count: payload.image_count,
            image_style: payload.image_style,
            use_brand_style: payload.use_brand_style,
            include_price: payload.include_price,
            include_address: payload.include_address,
            include_logo: payload.include_logo,
            extra_notes: payload.extra_notes,
            format_mode: payload.format_mode,
            price: payload.price,
            instagram_cta: cta || null,
          })
        : await crmApi.createStudioCampaign(token, payload);
      const generated = await crmApi.generateStudioCampaign(token, created.id);
      applyCampaign(generated);
      setTab("create");
      loadStudio();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось сгенерировать контент"));
    } finally {
      setBusy(false);
    }
  };

  const openCampaign = async (id) => {
    try {
      const next = await crmApi.getStudioCampaign(token, id);
      applyCampaign(next);
      setTab("create");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось открыть кампанию"));
    }
  };

  const startFresh = () => {
    setCampaign(null);
    setPrompt("");
    setChannels(["instagram_post", "whatsapp"]);
    setImageCount(1);
    setImageStyle("");
    setUseBrandStyle(true);
    setIncludePrice(false);
    setIncludeAddress(true);
    setIncludeLogo(false);
    setPrice("");
    setCta("");
    setExtraNotes("");
    setFormatMode("auto");
    setSavedAt(null);
    setTab("create");
  };

  const useTemplate = async (template) => {
    try {
      if (canWrite) await crmApi.useStudioTemplate(token, template.id);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отметить шаблон"));
    }
    setPrompt(template.prompt);
    setImageCount(template.image_count || 1);
    setImageStyle(template.style || "");
    if (template.cta) setCta(template.cta);
    if (template.default_channels) {
      setChannels(
        String(template.default_channels)
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      );
    }
    setTab("create");
  };

  const saveDraft = async () => {
    if (!campaign?.id) {
      toast.error("Сначала сгенерируйте контент.");
      return;
    }
    setBusy(true);
    try {
      const next = await crmApi.patchStudioCampaign(token, campaign.id, {
        title: campaign.title,
        prompt_text: prompt,
        instagram_caption: campaign.instagram_caption,
        instagram_cta: campaign.instagram_cta,
        instagram_hashtags: campaign.instagram_hashtags,
        whatsapp_text: campaign.whatsapp_text,
        channels,
      });
      applyCampaign(next);
      toast.success("Черновик сохранён");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось сохранить"));
    } finally {
      setBusy(false);
    }
  };

  const confirmSchedule = async () => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const when = localDateTime(scheduleDate, scheduleTime);
      const next = await crmApi.scheduleStudioCampaign(token, campaign.id, {
        scheduled_at: when.toISOString(),
        channels,
      });
      applyCampaign(next);
      setScheduleOpen(false);
      toast.success("Публикация запланирована");
      loadStudio();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось запланировать"));
    } finally {
      setBusy(false);
    }
  };

  const confirmPublish = async () => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const key = campaign.publish_idempotency_key || `ui-${campaign.id}-${Date.now()}`;
      const next = await crmApi.publishStudioCampaign(token, campaign.id, {
        channels,
        idempotency_key: key,
      });
      applyCampaign(next);
      setPublishOpen(false);
      loadStudio();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось опубликовать"));
    } finally {
      setBusy(false);
    }
  };

  const rewrite = async (field, action) => {
    if (!campaign?.id) return;
    setBusy(true);
    setRewriteMenu(null);
    try {
      const next = await crmApi.rewriteStudioText(token, campaign.id, { field, action });
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось изменить текст"));
    } finally {
      setBusy(false);
    }
  };

  const regenerateSlide = async (slideId, promptOverride) => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const next = await crmApi.regenerateStudioSlide(token, campaign.id, slideId, {
        prompt: promptOverride || undefined,
      });
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось перегенерировать изображение"));
    } finally {
      setBusy(false);
    }
  };

  const deleteSlide = async (slideId) => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const next = await crmApi.deleteStudioSlide(token, campaign.id, slideId);
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось удалить изображение"));
    } finally {
      setBusy(false);
    }
  };

  const moveSlide = async (index, direction) => {
    if (!campaign?.slides) return;
    const ids = campaign.slides.map((item) => item.id);
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= ids.length) return;
    const [moved] = ids.splice(index, 1);
    ids.splice(nextIndex, 0, moved);
    setBusy(true);
    try {
      const next = await crmApi.reorderStudioSlides(token, campaign.id, ids);
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось изменить порядок"));
    } finally {
      setBusy(false);
    }
  };

  const uploadSlide = async (file) => {
    if (!campaign?.id || !file) return;
    const form = new FormData();
    form.append("source_image", file);
    setBusy(true);
    try {
      const next = await crmApi.uploadStudioSlide(token, campaign.id, form);
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить изображение"));
    } finally {
      setBusy(false);
    }
  };

  const cloneCampaign = async (id) => {
    setBusy(true);
    try {
      const next = await crmApi.cloneStudioCampaign(token, id);
      applyCampaign(next);
      setTab("create");
      toast.success("Создана похожая публикация");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось создать копию"));
    } finally {
      setBusy(false);
    }
  };

  const retryChannel = async (channel) => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const next = await crmApi.retryStudioChannel(token, campaign.id, channel);
      applyCampaign(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось повторить публикацию"));
    } finally {
      setBusy(false);
    }
  };

  const generating = campaign?.status === "generating" || busy;
  const hasResult = campaign && ["ready", "scheduled", "publishing", "published", "partial", "failed", "generating"].includes(campaign.status);
  const slides = campaign?.slides || [];
  const progress = campaign?.generation_progress || {};

  return (
    <div className="space-y-6" data-testid="ai-content-studio">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">AI Content Studio</p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Контент и публикации</h1>
          <p className="mt-1 max-w-2xl text-slate-600">
            Создавайте контент с AI и публикуйте его в Instagram и WhatsApp.
          </p>
        </div>
        <Button onClick={startFresh} className="h-11 rounded-xl px-5" data-testid="studio-new-button">
          + Создать публикацию
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 rounded-2xl bg-white p-1 shadow-sm ring-1 ring-slate-200/80">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${
              tab === item.id ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "create" && !hasResult && (
        <CreateHome
          home={home}
          loading={loading}
          onOpen={openCampaign}
          onCreate={startFresh}
        />
      )}

      {tab === "create" && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 sm:p-8">
            <h2 className="text-2xl font-semibold text-slate-950">Создать публикацию</h2>
            <p className="mt-1 text-slate-500">
              Опишите, что хотите рассказать клиентам. AI подготовит изображения и текст для выбранных каналов.
            </p>

            <label className="mt-8 block text-sm font-medium text-slate-800">Что хотим опубликовать?</label>
            <Textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                if (campaign?.id) queueAutosave({ prompt_text: e.target.value });
              }}
              placeholder={PROMPT_PLACEHOLDER}
              className="mt-2 min-h-[180px] resize-y rounded-2xl border-slate-200 bg-slate-50/70 p-4 text-base"
              data-testid="studio-prompt"
            />

            <div className="mt-4">
              <p className="mb-2 text-sm font-medium text-slate-700">Шаблоны промптов</p>
              <div className="flex flex-wrap gap-2">
                {templates.filter((item) => item.is_active).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => useTemplate(item)}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-200"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <p className="mb-3 text-sm font-medium text-slate-800">Куда публикуем?</p>
              <div className="flex flex-wrap gap-3">
                {availableChannels.map((item) => {
                  const checked = channels.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleChannel(item.id)}
                      className={`rounded-2xl border px-4 py-3 text-left transition ${
                        checked ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >
                      <span className="mr-2">{checked ? "☑" : "☐"}</span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8">
              <p className="mb-3 text-sm font-medium text-slate-800">Количество изображений</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      setImageCount(n);
                      if (campaign?.id) queueAutosave({ image_count: n });
                    }}
                    className={`h-11 w-11 rounded-xl text-sm font-medium ${
                      imageCount === n ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="mt-6 text-sm font-medium text-slate-500 hover:text-slate-800"
              onClick={() => setExtrasOpen((v) => !v)}
            >
              {extrasOpen ? "Скрыть дополнительные настройки" : "Дополнительные настройки"}
            </button>
            {extrasOpen && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Стиль изображения">
                  <Input value={imageStyle} onChange={(e) => setImageStyle(e.target.value)} placeholder="светлый, каталог…" />
                </Field>
                <Field label="CTA">
                  <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Напишите нам в WhatsApp" />
                </Field>
                <Field label="Цена">
                  <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="15 900 ₸" />
                </Field>
                <Field label="Формат">
                  <select
                    value={formatMode}
                    onChange={(e) => setFormatMode(e.target.value)}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="auto">Автоматически</option>
                    <option value="square">Квадрат</option>
                    <option value="vertical">Вертикальный</option>
                  </select>
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={useBrandStyle} onChange={(e) => setUseBrandStyle(e.target.checked)} />
                  Использовать фирменный стиль
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />
                  Добавить цену
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={includeAddress} onChange={(e) => setIncludeAddress(e.target.checked)} />
                  Добавить адрес
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={includeLogo} onChange={(e) => setIncludeLogo(e.target.checked)} />
                  Добавить логотип
                </label>
                <div className="sm:col-span-2">
                  <Field label="Дополнительные пожелания">
                    <Textarea value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)} className="min-h-[80px]" />
                  </Field>
                </div>
              </div>
            )}

            {campaign?.status === "generating" && (
              <GenerationProgress stage={campaign.generation_stage} progress={progress} imageCount={imageCount} />
            )}

            {campaign?.status === "failed" && campaign.error_message && (
              <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{campaign.error_message}</p>
            )}

            {canWrite && (
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="mt-8 h-12 rounded-2xl px-6 text-base"
                data-testid="studio-generate-button"
              >
                {generating ? <Loader2 className="animate-spin" /> : <Sparkles />}
                Сгенерировать контент
              </Button>
            )}
          </section>

          {hasResult ? null : (
            <aside className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
              <p className="text-sm font-medium text-slate-500">Как это работает</p>
              <ol className="mt-4 space-y-3 text-sm text-slate-600">
                <li>1. Опишите товар или акцию своими словами.</li>
                <li>2. Выберите Instagram и WhatsApp.</li>
                <li>3. AI подготовит изображения и тексты.</li>
                <li>4. Поправьте, если нужно, и опубликуйте.</li>
              </ol>
            </aside>
          )}
        </div>
      )}

      {tab === "create" && hasResult && campaign?.status !== "generating" && (
        <ResultEditor
          campaign={campaign}
          canWrite={canWrite}
          busy={busy}
          savedAt={savedAt}
          rewriteMenu={rewriteMenu}
          setRewriteMenu={setRewriteMenu}
          onPatch={(patch) => {
            setCampaign((prev) => ({ ...prev, ...patch }));
            queueAutosave(patch);
          }}
          onTitle={(title) => {
            setCampaign((prev) => ({ ...prev, title }));
            queueAutosave({ title });
          }}
          onRewrite={rewrite}
          onRegenerate={regenerateSlide}
          onDeleteSlide={deleteSlide}
          onMoveSlide={moveSlide}
          onUploadClick={() => fileInput.current?.click()}
          onSave={saveDraft}
          onSchedule={() => setScheduleOpen(true)}
          onPublish={() => setPublishOpen(true)}
          onRetry={retryChannel}
        />
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadSlide(file);
          e.target.value = "";
        }}
      />

      {tab === "campaigns" && (
        <CampaignsTab
          campaigns={campaigns}
          filter={campaignFilter}
          onFilter={setCampaignFilter}
          onOpen={openCampaign}
          onClone={cloneCampaign}
          canWrite={canWrite}
        />
      )}

      {tab === "calendar" && (
        <CalendarTab
          month={calendarMonth}
          onMonth={setCalendarMonth}
          items={calendarItems}
          onOpen={openCampaign}
        />
      )}

      {tab === "templates" && (
        <TemplatesTab
          templates={templates}
          canWrite={canWrite}
          form={templateForm}
          setForm={setTemplateForm}
          onUse={useTemplate}
          onReload={loadStudio}
          token={token}
        />
      )}

      {tab === "settings" && <SettingsTab capabilities={capabilities} />}

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Запланировать публикацию</DialogTitle>
            <DialogDescription>Выберите дату и время. Каналы можно уточнить перед отправкой.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Дата">
              <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
            </Field>
            <Field label="Время">
              <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
            </Field>
          </div>
          <div className="space-y-2 text-sm">
            {availableChannels.map((item) => (
              <label key={item.id} className="flex items-center gap-2">
                <input type="checkbox" checked={channels.includes(item.id)} onChange={() => toggleChannel(item.id)} />
                {item.label}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Отмена</Button>
            <Button onClick={confirmSchedule} disabled={busy}>Запланировать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Опубликовать сейчас</DialogTitle>
            <DialogDescription>Будет опубликовано:</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm text-slate-700">
            {channels.includes("instagram_post") && <li>Instagram Post ✓</li>}
            {channels.includes("whatsapp") && <li>WhatsApp Status ✓</li>}
            <li>{slides.length || imageCount} изображения</li>
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)}>Отмена</Button>
            <Button onClick={confirmPublish} disabled={busy}>Опубликовать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function CreateHome({ home, loading, onOpen, onCreate }) {
  if (loading) {
    return <div className="rounded-3xl bg-white p-8 text-slate-500 shadow-sm ring-1 ring-slate-200/70">Загрузка…</div>;
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Ближайшие публикации</h3>
          <Button variant="ghost" size="sm" onClick={onCreate}>Создать</Button>
        </div>
        <div className="mt-4 space-y-3">
          {(home.upcoming || []).length === 0 && <p className="text-sm text-slate-500">Пока ничего не запланировано.</p>}
          {(home.upcoming || []).map((item) => (
            <button key={item.id} type="button" onClick={() => onOpen(item.id)} className="flex w-full items-center gap-3 rounded-2xl p-2 text-left hover:bg-slate-50">
              <Thumb path={coverOf(item)} />
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-900">{item.title}</p>
                <p className="text-sm text-slate-500">{formatDateTime(item.scheduled_at)}</p>
              </div>
            </button>
          ))}
        </div>
      </section>
      <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
        <h3 className="text-lg font-semibold">Последние кампании</h3>
        <div className="mt-4 space-y-3">
          {(home.recent || []).length === 0 && <p className="text-sm text-slate-500">Кампаний пока нет.</p>}
          {(home.recent || []).map((item) => (
            <button key={item.id} type="button" onClick={() => onOpen(item.id)} className="flex w-full items-center gap-3 rounded-2xl p-2 text-left hover:bg-slate-50">
              <Thumb path={coverOf(item)} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{item.title}</p>
                <p className="text-sm text-slate-500">{STATUS_LABELS[item.status] || item.status}</p>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function GenerationProgress({ stage, progress, imageCount }) {
  const steps = [
    { id: "analyzing", label: "Анализируем задание" },
    { id: "writing", label: "Формируем текст" },
    {
      id: "imaging",
      label: progress.images_done
        ? `Создаём изображения ${progress.images_done}/${progress.images_total || imageCount}`
        : "Создаём изображения",
    },
    { id: "adapting", label: "Подготавливаем версии для каналов" },
  ];
  const order = ["analyzing", "writing", "imaging", "adapting", "done"];
  const current = order.indexOf(stage || "analyzing");
  return (
    <div className="mt-6 space-y-2 rounded-2xl bg-slate-50 p-4">
      {steps.map((step, index) => {
        const done = current > index || stage === "done";
        const active = current === index;
        return (
          <div key={step.id} className="flex items-center gap-2 text-sm">
            <span className={`flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-white text-slate-400 ring-1 ring-slate-200"}`}>
              {done ? <Check className="h-3 w-3" /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            </span>
            <span className={done || active ? "text-slate-800" : "text-slate-400"}>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function ResultEditor({
  campaign,
  canWrite,
  busy,
  savedAt,
  rewriteMenu,
  setRewriteMenu,
  onPatch,
  onTitle,
  onRewrite,
  onRegenerate,
  onDeleteSlide,
  onMoveSlide,
  onUploadClick,
  onSave,
  onSchedule,
  onPublish,
  onRetry,
}) {
  const slides = campaign.slides || [];
  return (
    <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          value={campaign.title || ""}
          onChange={(e) => onTitle(e.target.value)}
          className="h-11 max-w-xl border-0 bg-transparent px-0 text-xl font-semibold shadow-none"
        />
        <p className="text-sm text-slate-400">{savedAt ? `Сохранено ${formatTime(savedAt)}` : ""}</p>
      </div>

      <div className="mt-6 grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          {campaign.publish_to_instagram_post && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium">Instagram</h3>
                <RewriteMenu field="instagram_caption" open={rewriteMenu} setOpen={setRewriteMenu} onPick={onRewrite} disabled={!canWrite || busy} />
              </div>
              <Textarea
                value={campaign.instagram_caption || ""}
                onChange={(e) => onPatch({ instagram_caption: e.target.value })}
                className="min-h-[140px] rounded-2xl"
              />
              <Field label="Призыв к действию">
                <Input value={campaign.instagram_cta || ""} onChange={(e) => onPatch({ instagram_cta: e.target.value })} />
              </Field>
              <div className="mt-3">
                <Field label="Хэштеги">
                  <Textarea
                    value={campaign.instagram_hashtags || ""}
                    onChange={(e) => onPatch({ instagram_hashtags: e.target.value })}
                    className="min-h-[70px] rounded-2xl"
                  />
                </Field>
              </div>
            </div>
          )}
          {campaign.publish_to_whatsapp && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium">WhatsApp Status</h3>
                <RewriteMenu field="whatsapp_text" open={rewriteMenu} setOpen={setRewriteMenu} onPick={onRewrite} disabled={!canWrite || busy} />
              </div>
              <Textarea
                value={campaign.whatsapp_text || ""}
                onChange={(e) => onPatch({ whatsapp_text: e.target.value })}
                className="min-h-[110px] rounded-2xl"
              />
            </div>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium">Изображения</h3>
              {canWrite && (
                <Button variant="outline" size="sm" onClick={onUploadClick}>
                  <ImagePlus /> Загрузить своё
                </Button>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {slides.map((slide, index) => {
                const src = crmStaticUrl(slide.cover_image_path || slide.final_image_path || slide.generated_image_path);
                return (
                  <div key={slide.id} className="overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200">
                    {src ? <img src={src} alt="" className="h-56 w-full object-cover" /> : <div className="flex h-56 items-center justify-center text-slate-400">Нет изображения</div>}
                    <div className="space-y-2 p-3">
                      <Input
                        value={slide.caption || ""}
                        onChange={(e) => {
                          const nextSlides = slides.map((item) => (item.id === slide.id ? { ...item, caption: e.target.value } : item));
                          onPatch({ slides: nextSlides });
                        }}
                        placeholder="Промпт изображения"
                      />
                      <div className="flex flex-wrap gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onRegenerate(slide.id, slide.caption)} disabled={busy}>Перегенерировать</Button>
                        <Button variant="ghost" size="sm" onClick={() => onMoveSlide(index, -1)} disabled={index === 0}><ChevronLeft /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onMoveSlide(index, 1)} disabled={index === slides.length - 1}><ChevronRight /></Button>
                        {src && (
                          <a href={src} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="sm"><Download /></Button>
                          </a>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => onDeleteSlide(slide.id)} disabled={busy}><Trash2 /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <PreviewPane campaign={campaign} />
      </div>

      {(campaign.status === "published" || campaign.status === "partial" || campaign.status === "failed") && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {campaignChannels(campaign).map((channel) => {
            const pub = latestPublication(campaign, channel);
            const ok = pub?.status === "published";
            return (
              <div key={channel} className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="font-medium">{channelLabel(channel)}</p>
                <p className={ok ? "text-emerald-700" : "text-amber-700"}>
                  {ok ? "✓ Опубликовано" : pub?.error_message || "⚠ Ошибка публикации"}
                </p>
                {!ok && canWrite && (
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => onRetry(channel)}>Повторить</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canWrite && (
        <div className="mt-8 flex flex-wrap gap-3">
          <Button variant="outline" onClick={onSave} disabled={busy}>Сохранить черновик</Button>
          <Button variant="outline" onClick={onSchedule} disabled={busy}>Запланировать</Button>
          <Button onClick={onPublish} disabled={busy} className="h-11 rounded-xl px-6">Опубликовать сейчас</Button>
        </div>
      )}
    </section>
  );
}

function RewriteMenu({ field, open, setOpen, onPick, disabled }) {
  const shown = open === field;
  return (
    <div className="relative">
      <Button variant="ghost" size="sm" disabled={disabled} onClick={() => setOpen(shown ? null : field)}>
        <Sparkles /> Улучшить
      </Button>
      {shown && (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200">
          {REWRITE_ACTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50"
              onClick={() => onPick(field, item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewPane({ campaign, channel, onChannel, index, onIndex }) {
  const [localChannel, setLocalChannel] = useState(channel || (campaign.publish_to_instagram_post ? "instagram_post" : "whatsapp"));
  const [localIndex, setLocalIndex] = useState(index || 0);
  const active = onChannel ? channel : localChannel;
  const setActive = onChannel || setLocalChannel;
  const slideIndex = onIndex ? index : localIndex;
  const setSlideIndex = onIndex || setLocalIndex;
  const slides = campaign.slides || [];
  const slide = slides[slideIndex] || slides[0];
  const src = crmStaticUrl(slide?.cover_image_path || slide?.final_image_path || slide?.generated_image_path);

  useEffect(() => {
    if (slideIndex >= slides.length) setSlideIndex(0);
  }, [slideIndex, slides.length, setSlideIndex]);

  return (
    <aside className="rounded-3xl bg-slate-100/80 p-5">
      <div className="mb-4 flex gap-2">
        {campaign.publish_to_instagram_post && (
          <button type="button" onClick={() => setActive("instagram_post")} className={`rounded-full px-3 py-1 text-sm ${active === "instagram_post" ? "bg-slate-950 text-white" : "bg-white"}`}>
            Instagram
          </button>
        )}
        {campaign.publish_to_whatsapp && (
          <button type="button" onClick={() => setActive("whatsapp")} className={`rounded-full px-3 py-1 text-sm ${active === "whatsapp" ? "bg-slate-950 text-white" : "bg-white"}`}>
            WhatsApp
          </button>
        )}
      </div>
      {active === "instagram_post" ? (
        <div className="mx-auto max-w-[320px] overflow-hidden rounded-[28px] bg-white shadow-xl">
          <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium">АвтоВАЗ.KZ</div>
          <div className="relative bg-slate-200">
            {src ? <img src={src} alt="" className="aspect-square w-full object-cover" /> : <div className="aspect-square" />}
            {slides.length > 1 && (
              <>
                <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1" onClick={() => setSlideIndex((slideIndex - 1 + slides.length) % slides.length)}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1" onClick={() => setSlideIndex((slideIndex + 1) % slides.length)}>
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                  {slides.map((item, i) => (
                    <span key={item.id} className={`h-1.5 w-1.5 rounded-full ${i === slideIndex ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="space-y-2 p-3 text-sm">
            <p className="whitespace-pre-wrap text-slate-800">{campaign.instagram_caption}</p>
            {campaign.instagram_cta && <p className="font-medium">{campaign.instagram_cta}</p>}
            {campaign.instagram_hashtags && <p className="text-sky-700">{campaign.instagram_hashtags}</p>}
          </div>
        </div>
      ) : (
        <div className="mx-auto w-[220px]">
          <div className="relative overflow-hidden rounded-[32px] bg-black shadow-xl">
            {src ? <img src={src} alt="" className="aspect-[9/16] w-full object-cover" /> : <div className="aspect-[9/16] bg-slate-800" />}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm text-white">
              <p className="whitespace-pre-wrap">{campaign.whatsapp_text}</p>
            </div>
            {slides.length > 1 && (
              <div className="absolute left-0 right-0 top-3 flex items-center justify-between px-3 text-xs text-white">
                <button type="button" onClick={() => setSlideIndex((slideIndex - 1 + slides.length) % slides.length)}><ChevronLeft /></button>
                <span>{slideIndex + 1} / {slides.length}</span>
                <button type="button" onClick={() => setSlideIndex((slideIndex + 1) % slides.length)}><ChevronRight /></button>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

function Thumb({ path }) {
  const src = crmStaticUrl(path);
  return src ? (
    <img src={src} alt="" className="h-12 w-12 rounded-xl object-cover" />
  ) : (
    <div className="h-12 w-12 rounded-xl bg-slate-100" />
  );
}

function CampaignsTab({ campaigns, filter, onFilter, onOpen, onClone, canWrite }) {
  return (
    <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onFilter(item.id)}
            className={`rounded-full px-3 py-1.5 text-sm ${filter === item.id ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="mt-6 divide-y divide-slate-100">
        {campaigns.length === 0 && <p className="py-8 text-center text-slate-500">Кампаний нет.</p>}
        {campaigns.map((item) => (
          <div key={item.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
            <Thumb path={coverOf(item)} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-950">{item.title}</p>
              <p className="text-sm text-slate-500">
                {campaignChannels(item).map(channelLabel).join(" • ") || "Без каналов"}
                {" · "}
                {item.slides_count || item.image_count || 0} изображения
              </p>
              <p className="text-sm text-slate-400">{formatDateTime(item.created_at)}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusClass(item.status)}`}>
              {STATUS_LABELS[item.status] || item.status}
            </span>
            {item.scheduled_at && <span className="text-sm text-slate-500">{formatDateTime(item.scheduled_at)}</span>}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpen(item.id)}>Открыть</Button>
              {canWrite && (
                <Button variant="ghost" size="sm" onClick={() => onClone(item.id)}>
                  <MoreHorizontal /> Создать похожую
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CalendarTab({ month, onMonth, items, onOpen }) {
  const days = monthGrid(month.year, month.month);
  const title = new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" }).format(new Date(month.year, month.month, 1));
  const byDay = {};
  items.forEach((item) => {
    if (!item.scheduled_at) return;
    const key = toDateInput(item.scheduled_at);
    byDay[key] = byDay[key] || [];
    byDay[key].push(item);
  });
  return (
    <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold capitalize">{title}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onMonth({ year: month.month === 0 ? month.year - 1 : month.year, month: month.month === 0 ? 11 : month.month - 1 })}>
            <ChevronLeft />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onMonth({ year: month.month === 11 ? month.year + 1 : month.year, month: month.month === 11 ? 0 : month.month + 1 })}>
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium uppercase text-slate-400">
        {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-2">
        {days.map((day) => {
          const key = toDateInput(day);
          const inMonth = day.getMonth() === month.month;
          const dayItems = byDay[key] || [];
          return (
            <div key={key} className={`min-h-[92px] rounded-2xl p-2 ${inMonth ? "bg-slate-50" : "bg-white text-slate-300"}`}>
              <div className="text-xs">{day.getDate()}</div>
              <div className="mt-1 space-y-1">
                {dayItems.map((item) => (
                  <button key={item.id} type="button" onClick={() => onOpen(item.id)} className="block w-full truncate rounded-lg bg-white px-1.5 py-1 text-left text-xs text-slate-700 ring-1 ring-slate-200">
                    {formatTime(item.scheduled_at)} {item.title}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TemplatesTab({ templates, canWrite, form, setForm, onUse, onReload, token }) {
  const empty = { name: "", prompt: "", default_channels: ["instagram_post", "whatsapp"], image_count: 1, style: "", cta: "", is_active: true };
  const save = async () => {
    try {
      if (form.id) {
        await crmApi.patchStudioTemplate(token, form.id, form);
      } else {
        await crmApi.createStudioTemplate(token, form);
      }
      setForm(null);
      onReload();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось сохранить шаблон"));
    }
  };
  return (
    <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Шаблоны промптов</h3>
        {canWrite && <Button onClick={() => setForm(empty)}>Новый шаблон</Button>}
      </div>
      {form && (
        <div className="mb-6 space-y-3 rounded-2xl bg-slate-50 p-4">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Название" />
          <Textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} className="min-h-[120px]" placeholder="Промпт" />
          <div className="grid gap-3 sm:grid-cols-3">
            <Input value={form.style || ""} onChange={(e) => setForm({ ...form, style: e.target.value })} placeholder="Стиль" />
            <Input value={form.cta || ""} onChange={(e) => setForm({ ...form, cta: e.target.value })} placeholder="CTA" />
            <Input type="number" min="1" max="5" value={form.image_count} onChange={(e) => setForm({ ...form, image_count: Number(e.target.value) })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Активен
          </label>
          <div className="flex gap-2">
            <Button onClick={save}>Сохранить</Button>
            <Button variant="outline" onClick={() => setForm(null)}>Отмена</Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {templates.map((item) => (
          <div key={item.id} className="rounded-2xl p-4 ring-1 ring-slate-200">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">{item.name}</p>
                <p className="mt-1 max-w-3xl text-sm text-slate-500">{item.prompt}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => onUse(item)}>Использовать</Button>
                {canWrite && <Button size="sm" variant="outline" onClick={() => setForm(item)}>Изменить</Button>}
                {canWrite && (
                  <Button size="sm" variant="outline" onClick={async () => { await crmApi.duplicateStudioTemplate(token, item.id); onReload(); }}>
                    Дублировать
                  </Button>
                )}
                {canWrite && !item.is_builtin && (
                  <Button size="sm" variant="ghost" onClick={async () => { await crmApi.deleteStudioTemplate(token, item.id); onReload(); }}>
                    Удалить
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SettingsTab({ capabilities }) {
  return (
    <section className="space-y-4">
      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
        <h3 className="text-lg font-semibold">Интеграции</h3>
        <p className="mt-1 text-sm text-slate-500">Технические ключи и токены находятся в настройках CRM, не на экране создания.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="font-medium">Instagram Post</p>
            <p className="text-sm text-slate-600">
              {capabilities?.instagram_post?.available ? "Подключен" : capabilities?.instagram_post?.reason || "Не настроен"}
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="font-medium">WhatsApp Status</p>
            <p className="text-sm text-slate-600">
              {capabilities?.whatsapp_status?.available ? "Подключен" : capabilities?.whatsapp_status?.reason || "Не настроен"}
            </p>
          </div>
        </div>
        <Link to="/crm/settings" className="mt-4 inline-block text-sm font-medium text-slate-700 underline">
          Открыть настройки CRM
        </Link>
      </div>
    </section>
  );
}
